import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export type TimeRange = 'live' | '1d' | '1w' | '1m' | '3m';

export interface TimeSeriesPoint {
  timeValue: number; // ms epoch — used as X-axis key
  ts: string;        // ISO string — used for dedup
  [key: string]: any;
}

interface UseTimeSeriesOptions<T> {
  symbol: string;
  range: TimeRange;
  // Called for non-live ranges to load historical data
  fetchHistorical: (symbol: string, lookback: string, token: string | null) => Promise<T[]>;
  // Map an API point → TimeSeriesPoint
  mapApiPoint: (p: T) => TimeSeriesPoint;
  // Live data from Redux (grows as ticks arrive)
  liveData: any[];
  // Map a live WS point → TimeSeriesPoint
  mapLivePoint: (p: any) => TimeSeriesPoint;
}

interface UseTimeSeriesResult {
  data: TimeSeriesPoint[];
  loading: boolean;
  error: string | null;
  isLive: boolean;
}

export function useTimeSeries<T>({
  symbol,
  range,
  fetchHistorical,
  mapApiPoint,
  liveData,
  mapLivePoint,
}: UseTimeSeriesOptions<T>): UseTimeSeriesResult {
  const { token } = useAuth();
  const [historicalData, setHistoricalData] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the timestamp of the last historical point so we can append only newer live ticks
  const lastHistoricalTsRef = useRef<number>(0);
  // Guard against stale fetches when symbol/range change mid-flight
  const fetchIdRef = useRef(0);

  const isLive = range === 'live';

  // Fetch historical data when symbol or range changes (skip for 'live')
  const fetchData = useCallback(async () => {
    if (isLive) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchHistorical(symbol, range, token);
      if (id !== fetchIdRef.current) return; // stale

      const points = rows.map(mapApiPoint).sort((a, b) => a.timeValue - b.timeValue);
      setHistoricalData(points);
      lastHistoricalTsRef.current = points.length > 0 ? points[points.length - 1].timeValue : 0;
    } catch (err) {
      if (id !== fetchIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setHistoricalData([]);
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [symbol, range, token, isLive, fetchHistorical, mapApiPoint]);

  useEffect(() => {
    lastHistoricalTsRef.current = 0;
    setHistoricalData([]);
    fetchData();
  }, [fetchData]);

  // For historical ranges, merge any live ticks that arrived after the last historical point
  const [liveTail, setLiveTail] = useState<TimeSeriesPoint[]>([]);
  const liveTailTsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isLive) {
      setLiveTail([]);
      liveTailTsRef.current = new Set();
      return;
    }
    if (loading || liveData.length === 0) return;

    const cutoff = lastHistoricalTsRef.current;
    const newTail: TimeSeriesPoint[] = [];

    for (const raw of liveData) {
      const pt = mapLivePoint(raw);
      if (pt.timeValue > cutoff && !liveTailTsRef.current.has(pt.ts)) {
        liveTailTsRef.current.add(pt.ts);
        newTail.push(pt);
      }
    }

    if (newTail.length > 0) {
      setLiveTail(prev => [...prev, ...newTail].sort((a, b) => a.timeValue - b.timeValue));
    }
  }, [liveData, loading, isLive, mapLivePoint]);

  // Reset live tail on symbol / range change
  useEffect(() => {
    setLiveTail([]);
    liveTailTsRef.current = new Set();
  }, [symbol, range]);

  // In live mode: return mapped live data directly
  if (isLive) {
    const livePts = liveData
      .map(mapLivePoint)
      .filter(p => p.timeValue > 0)
      .sort((a, b) => a.timeValue - b.timeValue);
    return { data: livePts, loading: false, error: null, isLive: true };
  }

  return {
    data: [...historicalData, ...liveTail],
    loading,
    error,
    isLive: false,
  };
}
